import axios from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import {
  fsnClientSearchResponseSchema,
  fsnImportActiveAttemptResponseSchema,
  fsnImportAttemptSchema,
  fsnImportEstimateSchema,
  fsnImportPlanSchema,
} from './fsn-imports.schema'
import type {
  FsnImportProductOptionsInput,
  FsnImportSection,
  FsnImportWebsiteNavigation,
} from './fsn-imports.types'

/** Imports act on one florist organization; global users run them from its route. */
interface FsnImportTarget {
  organizationId: number | string
}

export interface FsnClientSearchInput {
  /** At least two characters. */
  query: string
  /** At most 10. */
  limit?: number
}

/** `EstimateFsnImportRequest`. A blank username lets the API pick the client's website account. */
export interface FsnImportClientInput {
  fsnClientId: number
  fsnUsername?: string | null
}

/** `PlanFsnImportRequest`. Left-out choices take the planner's defaults. */
export interface FsnImportPlanInput extends FsnImportClientInput {
  sections?: FsnImportSection[]
  productOptions?: FsnImportProductOptionsInput
  websiteNavigation?: FsnImportWebsiteNavigation
}

/**
 * `StoreFsnImportAttemptRequest`: the previewed plan's fingerprint, and an attempt id the browser
 * creates so a retried start finds the same attempt.
 */
export interface FsnImportAttemptInput extends FsnImportPlanInput {
  planFingerprint: string
  importAttemptId?: string | null
}

export async function searchFsnClients({
  organizationId,
  query,
  limit = 10,
}: FsnImportTarget & FsnClientSearchInput) {
  const response = await axios.get(
    '/imports/fsn/clients',
    buildAdminRequestConfig({
      organizationId,
      params: {
        query,
        limit,
      },
    })
  )

  return fsnClientSearchResponseSchema.parse(response)
}

function buildFsnImportPayload({
  organizationId,
  fsnClientId,
  fsnUsername,
  sections,
  productOptions,
  websiteNavigation,
  planFingerprint,
  importAttemptId,
}: FsnImportTarget & FsnImportPlanInput & Partial<FsnImportAttemptInput>) {
  return {
    organization_id: Number(organizationId),
    fsn_client_id: Number(fsnClientId),
    sections,
    product_options: productOptions,
    website_navigation: websiteNavigation,
    ...(fsnUsername ? { fsn_username: fsnUsername } : {}),
    ...(planFingerprint ? { plan_fingerprint: planFingerprint } : {}),
    ...(importAttemptId ? { import_attempt_id: importAttemptId } : {}),
  }
}

export async function previewFsnImportPlan({
  organizationId,
  fsnClientId,
  fsnUsername,
  sections,
  productOptions,
  websiteNavigation,
}: FsnImportTarget & FsnImportPlanInput) {
  const response = await axios.post(
    '/imports/fsn/plan',
    buildFsnImportPayload({
      organizationId,
      fsnClientId,
      fsnUsername,
      sections,
      productOptions,
      websiteNavigation,
    }),
    buildAdminRequestConfig({ organizationId })
  )

  return fsnImportPlanSchema.parse(response)
}

export async function getFsnImportEstimate({
  organizationId,
  fsnClientId,
  fsnUsername,
}: FsnImportTarget & FsnImportClientInput) {
  const response = await axios.post(
    '/imports/fsn/estimate',
    buildFsnImportPayload({
      organizationId,
      fsnClientId,
      fsnUsername,
    }),
    buildAdminRequestConfig({ organizationId })
  )

  return fsnImportEstimateSchema.parse(response)
}

export async function startFsnImportAttempt({
  organizationId,
  fsnClientId,
  fsnUsername,
  sections,
  productOptions,
  websiteNavigation,
  planFingerprint,
  importAttemptId,
}: FsnImportTarget & FsnImportAttemptInput) {
  const response = await axios.post(
    '/imports/fsn/import-attempts',
    buildFsnImportPayload({
      organizationId,
      fsnClientId,
      fsnUsername,
      sections,
      productOptions,
      websiteNavigation,
      planFingerprint,
      importAttemptId,
    }),
    buildAdminRequestConfig({ organizationId })
  )

  return fsnImportAttemptSchema.parse(response)
}

export async function getActiveFsnImportAttempt({ organizationId }: FsnImportTarget) {
  const response = await axios.get(
    '/imports/fsn/import-attempts/active',
    buildAdminRequestConfig({ organizationId })
  )

  return fsnImportActiveAttemptResponseSchema.parse(response).attempt
}

export async function getFsnImportAttempt({
  organizationId,
  importAttemptId,
}: FsnImportTarget & { importAttemptId: string }) {
  const response = await axios.get(
    `/imports/fsn/import-attempts/${importAttemptId}`,
    buildAdminRequestConfig({ organizationId })
  )

  return fsnImportAttemptSchema.parse(response)
}
